/**
 * @file Login/Logout authentication buttons component
 * @version 1.0.0
 * @since 2025-08-25
 */

import { useUser } from '../composables/user.js';
import { useNotifications, useErrorHandler } from '../../core/errors.js';

export default {
  name: 'UserAuthButtons',

  template: `
    <div>
      <button 
        v-if="!user.loggedIn"
        class="btn btn-primary"
        @click="handleLogin"
        :disabled="user.isLoading"
      >
        <i class="bi bi-box-arrow-in-right me-2"></i>
        {{ user.isLoading ? 'Loading...' : 'Login' }}
      </button>
      
      <button 
        v-else-if="user.allowLogout"
        class="btn btn-outline-secondary"
        @click="handleLogout"
        :disabled="user.isLoading"
      >
        <i class="bi bi-box-arrow-right me-2"></i>
        Logout
      </button>
      
      <button 
        v-else
        class="btn btn-outline-primary"
        @click="handleRefresh"
        :disabled="user.isLoading"
      >
        <i class="bi bi-arrow-clockwise me-2"></i>
        {{ user.isLoading ? 'Loading...' : 'Refresh' }}
      </button>
    </div>
  `,

  setup() {
    const user = useUser();
    const { success, info } = useNotifications();
    const { handleError } = useErrorHandler();

    const handleLogin = async () => {
      try {
        await user.login();
        success(`Logged in as ${user.name}`);
      } catch (err) {
        handleError(err, { operation: 'login' });
      }
    };

    const handleLogout = async () => {
      if (!user.allowLogout) {
        return;
      }
      try {
        await user.logout();
        info('Logged out');
      } catch (err) {
        handleError(err, { operation: 'logout' });
      }
    };

    const handleRefresh = async () => {
      try {
        await user.refresh();
        success(`Refreshed user data for ${user.name}`);
      } catch (err) {
        handleError(err, { operation: 'refresh' });
      }
    };

    return {
      user,
      handleLogin,
      handleLogout,
      handleRefresh,
    };
  },
};
